/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import type { IStore } from '@repo/common/types/Store.d';

export class Store implements IStore {
  private readonly storageKey: string;

  constructor(storageKey: string) {
    this.storageKey = storageKey;
  }

  // Save a key-value pair to the store
  async set(key: string, value: unknown): Promise<void> {
    const data = this._getData();
    data[key] = value;
    this._setData(data);
  }

  // Get a value by key
  async get<T>(key: string): Promise<T | undefined> {
    const data = this._getData();
    return (data[key] as T) ?? undefined; // Return the value or undefined if not found
  }

  // Delete a key-value pair from the store
  async delete(key: string): Promise<boolean> {
    const data = this._getData();
    if (key in data) {
      delete data[key]; // Remove the key from the data object
      this._setData(data); // Save the updated data back to localStorage
      return true;
    }
    return false;
  }

  // Get all entries from the store as an array of [key, value]
  async entries<T>(): Promise<[string, T][]> {
    const data = this._getData();
    return Object.entries(data) as [string, T][];
  }

  // Commit/save the current state of the store (no-op in this case as LocalStorage persists)
  async save(): Promise<void> {
    // No explicit action required for localStorage, but could include synchronization logic if needed
    return Promise.resolve();
  }

  // Reload the store from localStorage
  async reload(): Promise<void> {
    // No-op for localStorage as it's always in sync
    return Promise.resolve();
  }

  // Clear all data from the store
  async clear(): Promise<void> {
    localStorage.removeItem(this.storageKey);
  }

  // Reset the store to default (same as clear for localStorage)
  async reset(): Promise<void> {
    localStorage.removeItem(this.storageKey);
  }

  // Private helper to get all data from localStorage under the store's key
  private _getData(): Record<string, unknown> {
    const jsonData = localStorage.getItem(this.storageKey);
    return jsonData ? JSON.parse(jsonData) : {};
  }

  // Private helper to set data in localStorage under the store's key
  private _setData(data: Record<string, unknown>): void {
    localStorage.setItem(this.storageKey, JSON.stringify(data));
  }
}
